import React, { Component } from 'react';
import { func, number, string, array } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import { Tabs, AnswerIcon } from '@snapask/common-components';

import {
  getTutorStatisticalData,
  getAverageRate,
  getAnswerCount,
  getSinceDate,
  getFetchProfileStatus,
  getBadges,
  getCurriculums,
} from '@dragonfruit/redux/profile';
import { READY } from '@dragonfruit/constants/loadingState';
import ProgressBar from '@dragonfruit/components/ProgressBar';
import StarPicker from '@dragonfruit/components/StarPicker';

import AccountCard from './components/AccountCard';
import {
  ANSWER_COUNT,
  AVERAGE_RATE,
  BADGE_COLORS,
  BADGE_IMAGES,
} from './constants/tutorBadges';

import progressBarStyles from './progressBarStyles.scss';
import styles from './AccountAchievements.scss';

const cx = require('classnames/bind').bind(styles);

const mapStateToProps = state => ({
  averageRate: getAverageRate(state),
  answerCount: getAnswerCount(state),
  sinceDate: getSinceDate(state),
  status: getFetchProfileStatus(state),
  tutorBadges: getBadges(state),
  curriculums: getCurriculums(state),
});

const transformTabs = curriculums =>
  curriculums.map(curriculum => ({
    key: curriculum.name,
    title: curriculum.name,
  }));

const getActiveTab = curriculums => {
  const activeTab = curriculums.length ? transformTabs(curriculums)[0] : null;

  return activeTab;
};

const getTabs = curriculums => {
  const tabs = curriculums.length ? transformTabs(curriculums) : [];

  return tabs;
};

class AccountAchievements extends Component {
  static propTypes = {
    getTutorStatisticalData: func.isRequired,
    status: string.isRequired,
    t: func.isRequired,
    answerCount: number,
    averageRate: number,
    curriculums: array,
    sinceDate: string,
    tutorBadges: array,
  };

  static defaultProps = {
    answerCount: 0,
    averageRate: 0,
    curriculums: [],
    sinceDate: '',
    tutorBadges: [],
  };

  state = {
    activeTab: getActiveTab(this.props.curriculums),
    tabs: getTabs(this.props.curriculums),
  };

  componentDidMount = () => {
    this.props.getTutorStatisticalData();
  };

  componentWillReceiveProps(nextProps) {
    const { curriculums } = nextProps;

    if (curriculums.length === this.props.curriculums.length) {
      return;
    }

    this.setState({
      activeTab: getActiveTab(curriculums),
      tabs: getTabs(curriculums),
    });
  }

  onTabSelect = activeTab => {
    this.setState({
      activeTab,
    });
  };

  renderBadge = badge => {
    const { id, name, description } = badge;

    return (
      <div key={id} className={cx(styles.cardContainer, styles.badgeContainer)}>
        <AccountCard borderColor={BADGE_COLORS[name]}>
          <div className={styles.image}>
            <img src={BADGE_IMAGES[name]} alt={description} />
          </div>
          <div className={styles.text}>
            <p>{description}</p>
          </div>
        </AccountCard>
      </div>
    );
  };

  renderProgressRow = (subject, index, displayCurrs) => (
    <div key={`curriculum-${index}`} className={styles.subjectRow}>
      <div className={styles.subjectInfos}>
        <div className={styles.name}>
          {subject.name}
          <div className={styles.mobileStar}>
            <StarPicker value={subject.averageRate} count={1} />
          </div>
        </div>
        <div className={styles.average}>
          {`${subject.answerCount} / ${displayCurrs.answerCount}`}
        </div>
      </div>
      <div>
        <ProgressBar
          theme={progressBarStyles}
          total={displayCurrs.answerCount}
          completion={subject.answerCount}
        />
        <div className={styles.starInfo}>
          <StarPicker value={subject.averageRate} />
          {Math.round(subject.averageRate * 100) / 100}
        </div>
      </div>
    </div>
  );

  render() {
    const {
      t,
      status,
      sinceDate,
      answerCount,
      averageRate,
      tutorBadges,
      curriculums,
    } = this.props;

    const { activeTab, tabs } = this.state;

    const [displayCurrs] = curriculums.filter(
      curriculum => curriculum.name === activeTab.key
    );

    return (
      <div className={styles.mainRoot}>
        <div className={styles.badgesContainer}>
          {!!answerCount && status === READY && (
            <div className={cx(styles.cardContainer, styles.textContainer)}>
              <AccountCard borderColor={BADGE_COLORS[ANSWER_COUNT]}>
                <h1 className={styles.topSection}>{answerCount}</h1>
                <div className={styles.text}>
                  <p>{t('account.answeredQuestions')}</p>
                  <p className={styles.small}>
                    {t('account.sinced')}
                    <br />
                    {sinceDate}
                  </p>
                </div>
              </AccountCard>
            </div>
          )}
          {!!averageRate && status === READY && (
            <div className={cx(styles.cardContainer, styles.textContainer)}>
              <AccountCard borderColor={BADGE_COLORS[AVERAGE_RATE]}>
                <h1 className={styles.topSection}>{averageRate}</h1>
                <div className={styles.text}>
                  <p>{t('account.averageRating')}</p>
                </div>
              </AccountCard>
            </div>
          )}
          {!!tutorBadges.length &&
            status === READY &&
            tutorBadges.map(this.renderBadge)}
        </div>
        {!!curriculums.length && status === READY && (
          <div className={styles.subjectCard}>
            {activeTab && (
              <Tabs
                tabs={tabs}
                onTabSelect={this.onTabSelect}
                activeTab={activeTab}
                theme={styles}
              />
            )}
            {activeTab && (
              <div className={styles.dashBoard}>
                <div className={styles.summary}>
                  <AnswerIcon width={40} height={40} />
                  <div className={styles.info}>{displayCurrs.answerCount}</div>
                  <div className={styles.text}>
                    <div className={styles.questionAnswered}>
                      {t('account.answered')}
                    </div>
                  </div>
                </div>
                {displayCurrs.subjects.map((subject, index) =>
                  this.renderProgressRow(subject, index, displayCurrs)
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default compose(
  translate(['account']),
  connect(mapStateToProps, {
    getTutorStatisticalData,
  })
)(AccountAchievements);



// WEBPACK FOOTER //
// ./app/containers/Account/children/AccountAchievements/AccountAchievements.jsx